import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";
import SeoMeta from "@/components/SeoMeta";
import ScienceNav from "./ScienceNav";
import { ArrowRight, Newspaper } from "@phosphor-icons/react";

const INTRO = `Science Today is the news desk of Science Pulse — dated stories about real discoveries, missions and breakthroughs, kept separate from the evergreen concept map. Every story links back into the map, so a headline can become a whole learning path.`;

export default function ScienceToday() {
  const [news, setNews] = useState(null);
  const [pillars, setPillars] = useState({});

  useEffect(() => {
    http.get("/science/today", { params: { limit: 30 } })
      .then(({ data }) => setNews(data.news || []))
      .catch(() => setNews([]));
    http.get("/science/taxonomy")
      .then(({ data }) => setPillars(Object.fromEntries((data.pillars || []).map((p) => [p.slug, p]))))
      .catch(() => {});
  }, []);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="science-today">
      <div className="max-w-[1000px] mx-auto px-5 md:px-10">
        <ScienceNav />
        <SeoMeta
          title="Science Today — the news desk"
          description="Dated stories about real discoveries, missions and breakthroughs — each one linked back into the evergreen Science Pulse concept map."
          canonical={`${window.location.origin}/science/today`}
          jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", name: "Science Today", url: `${window.location.origin}/science/today` }}
        />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2 flex items-center gap-1.5">
          <Newspaper size={13} weight="fill" /> / The news desk
        </div>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.0] tracking-tight">Science Today.</h1>
        <p className="mt-3 max-w-2xl text-[#3A3830] text-sm md:text-base">{INTRO}</p>
        <div className="mt-3"><ListenTranslateBar text={INTRO} testidPrefix="science-today-tools" /></div>
        <div className="mt-6"><PageGuide pageKey="science:today" testidPrefix="science-today" /></div>

        {!news ? (
          <div className="mt-10 font-mono-tech text-[10px] tracking-[0.3em] uppercase" data-testid="science-today-loading">Loading the news desk…</div>
        ) : news.length === 0 ? (
          <p className="mt-10 text-sm text-[#3A3830]" data-testid="science-today-empty">No stories on the desk yet — check back soon.</p>
        ) : (
          <div className="mt-8 space-y-4" data-testid="science-today-feed">
            {news.map((n) => {
              const p = pillars[n.pillar];
              return (
                <Link
                  key={n.concept_id}
                  to={`/science/concept/${n.concept_id}`}
                  data-testid={`science-news-${n.concept_id}`}
                  className="group block rounded-2xl bg-white border border-black/10 p-5 md:p-6 hover:border-[color:var(--np-brand,#B85F2A)] hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex flex-wrap items-center gap-2 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]/70">
                    <span className="text-[color:var(--np-brand,#B85F2A)]">{n.published_at ? n.published_at.slice(0, 10) : ""}</span>
                    {p && <span className="rounded-full border border-black/10 px-2 py-0.5">{p.emoji} {p.label}</span>}
                    <span className="rounded-full border border-black/10 px-2 py-0.5">{n.evidence_label?.replace("-", " ")}</span>
                  </div>
                  <div className="mt-2 font-serif-display text-2xl leading-tight">{n.title}</div>
                  <p className="mt-2 text-sm text-[#3A3830]">{n.quick_answer}</p>
                  <div className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    Read the full explainer <ArrowRight size={11} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
